import { useNavigate } from "react-router-dom";
import { Bell, UserPlus, Heart, BookOpen, MapPin, Plane } from "lucide-react";
import { NotificationBell as SharedNotificationBell } from "@tabipla/components";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from "@/application/hooks/useNotifications";
import type { NotificationResponseDto, NotificationType } from "@tabipla/shared";

// Mekke-specific icon mapping - uses Bell for irrelevant notification types
const mekkeNotificationIcons: Partial<Record<NotificationType, typeof Bell>> = {
  group_invite: Bell,
  member_joined: Bell,
  poll_created: Bell,
  poll_closed: Bell,
  schedule_created: Bell,
  schedule_response: Bell,
  expense_added: Bell,
  chat_message: Bell,
  trip_updated: Plane,
  trip_invite: Plane,
  spot_added: MapPin,
  payment_request: Bell,
  payment_completed: Bell,
  new_follower: UserPlus,
  spot_liked: Heart,
  booklet_liked: BookOpen,
};

export function NotificationBell() {
  const navigate = useNavigate();

  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  const handleNavigate = (notification: NotificationResponseDto) => {
    // Mark as read
    if (!notification.read) {
      markRead.mutate(notification.id);
    }

    // Navigate to follower's profile if available
    if (notification.type === "new_follower" && notification.data?.followerId) {
      navigate(`/users/${notification.data.followerId}`);
    }
  };

  return (
    <SharedNotificationBell
      notifications={notifications}
      unreadCount={unreadCount}
      onMarkRead={(id) => markRead.mutate(id)}
      onMarkAllRead={() => markAllRead.mutate()}
      onDelete={(id) => deleteNotification.mutate(id)}
      onNavigate={handleNavigate}
      isMarkAllReadPending={markAllRead.isPending}
      notificationIcons={mekkeNotificationIcons}
    />
  );
}
